import { useMemo, useState } from 'react';
import { airtimeMinimum, airtimePresets, ownAirtimeNumber } from '@/mocks/airtime';
import {
  createAirtimeContact,
  loadAirtimeContacts,
  persistAirtimeContacts,
} from '@/utils/airtimeStore';
import { formatMoney } from '@/utils/format';
import type { AirtimeContact, FundingWallet, SinglePayment } from '@/types/portal';
import type { PaymentSentMeta } from '../types';
import AirtimeFormStep from './AirtimeFormStep';
import AirtimeConfirmStep from './AirtimeConfirmStep';
import AirtimeFavouritesModal from './AirtimeFavouritesModal';
import { imageUrl } from '@/utils/assets';

type AirtimeView = 'form' | 'confirm';

interface BuyAirtimeSheetProps {
  wallets: FundingWallet[];
  canManage: boolean;
  canApprove: boolean;
  counter: number;
  onPaymentSent: (payment: SinglePayment, meta: PaymentSentMeta) => void;
  onClose: () => void;
}

/** Excise/interest charged on an airtime purchase, as a share of the amount. */
const INTEREST_RATE = 0.01;

const AIRTIME_AVATAR = imageUrl('payments/buy-airtime-sim.webp');

/**
 * Dedicated "Buy Airtime" sheet. Choose who to top up (own line or another
 * number), capture the amount with one-tap presets, then show an "Airtime Top
 * Up" confirmation before submitting. The host page owns open/close state; this
 * component owns the two-screen stack.
 */
export default function BuyAirtimeSheet({
  wallets,
  canManage,
  canApprove,
  counter,
  onPaymentSent,
  onClose,
}: BuyAirtimeSheetProps) {
  const [view, setView] = useState<AirtimeView>('form');
  const [contacts, setContacts] = useState<AirtimeContact[]>(() => loadAirtimeContacts());
  const [manageOpen, setManageOpen] = useState(false);
  const [ownNumber, setOwnNumber] = useState(true);
  const [number, setNumber] = useState('');
  const [label, setLabel] = useState('Airtime Top Up');
  const [amount, setAmount] = useState(0);
  const [walletId, setWalletId] = useState(
    () => wallets.find((wallet) => wallet.currency === 'KES')?.id ?? wallets[0]?.id ?? '',
  );

  const favourites = useMemo(
    () => contacts.filter((contact) => contact.favourite),
    [contacts],
  );
  const wallet = wallets.find((item) => item.id === walletId) ?? null;
  const money = wallet?.currency ?? 'KES';

  const transactionCost = 0;
  const interestFee = amount > 0 ? Math.round(amount * INTEREST_RATE * 100) / 100 : 0;
  const total = amount + interestFee;
  const shortfall = wallet ? Math.max(0, total - wallet.balance) : 0;
  const covered = wallet !== null && shortfall <= 0;

  const numberValid = /^\d{9,12}$/.test(number);
  const amountValid = amount >= airtimeMinimum;

  const error = useMemo(() => {
    if (!canManage) return '';
    if (!wallet) return 'Select a wallet to top up from.';
    if (!ownNumber && !numberValid) return 'Enter a valid phone number.';
    if (!amountValid) return `Minimum top up amount is Ksh. ${airtimeMinimum}.`;
    if (!covered) return `Funding wallet is short by ${formatMoney(shortfall, money)}.`;
    return '';
  }, [canManage, wallet, ownNumber, numberValid, amountValid, covered, shortfall, money]);

  const canContinue = canManage && error === '';

  const handleOwnNumber = (value: boolean) => {
    setOwnNumber(value);
    if (value) {
      setNumber(ownAirtimeNumber);
      setLabel('Airtime Top Up');
    } else {
      setNumber('');
      setLabel('Airtime Top Up');
    }
  };

  const handlePickContact = (contact: AirtimeContact) => {
    setNumber(contact.number);
    setLabel('Airtime Top Up');
  };

  const handleToggleFavourite = (id: string) => {
    const next = contacts.map((contact) =>
      contact.id === id ? { ...contact, favourite: !contact.favourite } : contact,
    );
    setContacts(next);
    persistAirtimeContacts(next);
  };

  const handleAddContact = (name: string, number: string) => {
    const next = [...contacts, createAirtimeContact(name, number)];
    setContacts(next);
    persistAirtimeContacts(next);
  };

  const handleConfirm = () => {
    if (!wallet || !canContinue) return;
    const status = canApprove ? 'Paid' : 'Awaiting approval';
    const payment: SinglePayment = {
      id: `sp-${counter}`,
      reference: `ATU-${counter}`,
      payeeName: `Airtime · ${number}`,
      avatar: AIRTIME_AVATAR,
      channel: 'airtime',
      target: number,
      amount,
      currency: money,
      rail: `Airtime · ${money}`,
      status,
      createdAt: '16 Sep 2026',
      createdBy: 'Peter Kironji',
    };
    onPaymentSent(payment, { newPayee: null, request: { sourceWalletId: wallet.id, destination: { number } } });
  };

  return (
    <div>
      {/* Sheet header */}
      <div className="mb-6 flex items-center gap-3 border-b border-background-200 pb-4">
        {view === 'confirm' ? (
          <button
            type="button"
            onClick={() => setView('form')}
            aria-label="Back"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-background-200"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
        ) : null}
        <h2 className="flex-1 font-heading text-base font-semibold text-foreground-950">
          {view === 'confirm' ? 'Confirm' : 'Buy Airtime'}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-900"
        >
          <i className="ri-close-line text-lg" />
        </button>
      </div>

      {view === 'form' ? (
        <AirtimeFormStep
          favourites={favourites}
          onManageFavourites={() => setManageOpen(true)}
          onPickContact={handlePickContact}
          ownNumber={ownNumber}
          onOwnNumber={handleOwnNumber}
          ownLine={ownAirtimeNumber}
          number={number}
          onNumber={setNumber}
          amount={amount}
          onAmount={setAmount}
          presets={airtimePresets}
          minimum={airtimeMinimum}
          currency={money}
          wallets={wallets}
          walletId={walletId}
          onWallet={setWalletId}
          fee={transactionCost}
          total={total}
          error={error}
          canManage={canManage}
          canContinue={canContinue}
          onContinue={() => setView('confirm')}
        />
      ) : (
        <AirtimeConfirmStep
          number={number}
          label={label}
          amount={amount}
          currency={money}
          transactionCost={transactionCost}
          interestFee={interestFee}
          total={total}
          wallet={wallet}
          canApprove={canApprove}
          onConfirm={handleConfirm}
        />
      )}

      <AirtimeFavouritesModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        contacts={contacts}
        onToggle={handleToggleFavourite}
        onAdd={handleAddContact}
      />
    </div>
  );
}